import { format, parseISO } from 'date-fns';
import { AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronRight, Leaf, Box } from 'lucide-react';
import { useState } from 'react';
import type { RegionForecast, SpeciesForecast, ForecastWeek, SpeciesForecastWeek } from '../../types/shared';

interface Props {
  forecast: RegionForecast[];
}

export default function ForecastSection({ forecast }: Props) {
  // Only show regions that have stock set or upcoming consumption
  const activeRegions = forecast.filter(
    r =>
      r.pots > 0 ||
      r.species.some(s => s.currentQuantity > 0) ||
      r.potWeeklyBreakdown.some(w => w.consumptionThisWeek > 0) ||
      r.species.some(s => s.weeklyBreakdown.some(w => w.consumptionThisWeek > 0)),
  );

  if (activeRegions.length === 0) {
    return (
      <div className="text-center py-12 text-stone-500 dark:text-stone-400 text-sm">
        No stock levels set yet. Use the Stock tab to enter initial quantities for each region.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activeRegions.map(region => (
        <RegionForecastCard key={region.regionId} region={region} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Region forecast card
// ---------------------------------------------------------------------------

function RegionForecastCard({ region }: { region: RegionForecast }) {
  // Determine worst status across all items in this region
  const allStockoutDates = [
    region.potStockoutDate,
    ...region.species.map(s => s.stockoutDate),
  ];
  const worstStatus = allStockoutDates.reduce<StockoutStatus>((worst, date) => {
    const s = getStockoutStatus(date);
    return s.level > worst.level ? s : worst;
  }, getStockoutStatus(null));

  const atRiskCount = allStockoutDates.filter(d => d !== null).length;

  return (
    <div className={`rounded-xl border ${worstStatus.borderClass} bg-white dark:bg-[#1e2521]`}>
      {/* Card header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-100 dark:border-stone-800">
        <worstStatus.Icon size={18} className={worstStatus.iconClass} />
        <span className="font-semibold text-stone-900 dark:text-stone-100 flex-1">
          {region.regionName}
        </span>
        {atRiskCount > 0 && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${worstStatus.badgeClass}`}>
            {atRiskCount} item{atRiskCount !== 1 ? 's' : ''} at risk
          </span>
        )}
      </div>

      {/* Items */}
      <div className="divide-y divide-stone-100 dark:divide-stone-800">
        {/* Pot row */}
        <ForecastItemRow
          icon={<Box size={13} className="text-amber-400 shrink-0" />}
          label="Pots"
          currentQuantity={region.pots}
          stockoutDate={region.potStockoutDate}
          weeklyBreakdown={region.potWeeklyBreakdown}
          renderBreakdownRow={(week) => <PotBreakdownRow key={week.weekStarting} week={week as ForecastWeek} />}
          breakdownHeaders={['Week of', 'Workshops', 'Consumed', 'Pots left']}
        />

        {/* Species rows */}
        {region.species.map(species => (
          <SpeciesForecastRow key={species.speciesKey} species={species} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic forecast item row (used for pots and species)
// ---------------------------------------------------------------------------

interface ForecastItemRowProps {
  icon: React.ReactNode;
  label: string;
  currentQuantity: number;
  stockoutDate: string | null;
  weeklyBreakdown: Array<ForecastWeek | SpeciesForecastWeek>;
  breakdownHeaders: string[];
  renderBreakdownRow: (week: ForecastWeek | SpeciesForecastWeek) => React.ReactNode;
}

function ForecastItemRow({
  icon,
  label,
  currentQuantity,
  stockoutDate,
  weeklyBreakdown,
  breakdownHeaders,
  renderBreakdownRow,
}: ForecastItemRowProps) {
  const [expanded, setExpanded] = useState(false);
  const status = getStockoutStatus(stockoutDate);
  const hasActivity = weeklyBreakdown.some(w => w.consumptionThisWeek > 0);

  return (
    <div>
      <div className="flex items-center px-5 py-3 gap-3">
        {icon}
        <span className="flex-1 text-sm text-stone-700 dark:text-stone-300">{label}</span>

        <div className="flex items-center gap-4">
          <span className="text-sm tabular-nums text-stone-500 dark:text-stone-400">
            {currentQuantity.toLocaleString()} on hand
          </span>

          {currentQuantity === 0 ? (
            <span className="text-xs text-stone-400 dark:text-stone-500 italic">Not set</span>
          ) : stockoutDate === null ? (
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              Stock holds 90 days
            </span>
          ) : (
            <span className={`text-xs font-semibold ${status.textClass}`}>
              Stockout {format(parseISO(stockoutDate), 'MMM d')}
            </span>
          )}

          {hasActivity && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition ml-1"
              title={expanded ? 'Collapse weekly breakdown' : 'Expand weekly breakdown'}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
        </div>
      </div>

      {expanded && hasActivity && (
        <div className="border-t border-stone-100 dark:border-stone-800 ml-8 mr-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-stone-50 dark:bg-[#1a201d]">
                {breakdownHeaders.map(h => (
                  <th
                    key={h}
                    className={`px-4 py-2 font-semibold text-stone-500 dark:text-stone-400 ${
                      h === 'Week of' ? 'text-left' : 'text-right'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {weeklyBreakdown.map(week => renderBreakdownRow(week))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Species forecast row (wraps ForecastItemRow)
// ---------------------------------------------------------------------------

function SpeciesForecastRow({ species }: { species: SpeciesForecast }) {
  return (
    <ForecastItemRow
      icon={<Leaf size={13} className="text-moss-400 shrink-0" />}
      label={species.speciesName}
      currentQuantity={species.currentQuantity}
      stockoutDate={species.stockoutDate}
      weeklyBreakdown={species.weeklyBreakdown}
      breakdownHeaders={['Week of', 'Workshops', 'Consumed', 'Trees left']}
      renderBreakdownRow={(week) => (
        <SpeciesBreakdownRow
          key={week.weekStarting}
          week={week as SpeciesForecastWeek}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Weekly breakdown rows
// ---------------------------------------------------------------------------

function PotBreakdownRow({ week }: { week: ForecastWeek }) {
  return (
    <tr
      className={`bg-white dark:bg-[#1e2521] ${
        week.projectedPots === 0 ? 'bg-red-50 dark:bg-red-900/10' : ''
      }`}
    >
      <td className="px-4 py-2 text-stone-700 dark:text-stone-300">
        {format(parseISO(week.weekStarting), 'MMM d')}
      </td>
      <td className="px-4 py-2 text-right text-stone-600 dark:text-stone-400">
        {week.workshopsCount > 0 ? week.workshopsCount : '—'}
      </td>
      <td className="px-4 py-2 text-right text-stone-600 dark:text-stone-400">
        {week.consumptionThisWeek > 0 ? week.consumptionThisWeek.toLocaleString() : '—'}
      </td>
      <td className="px-4 py-2 text-right">
        <BalanceCell value={week.projectedPots} />
      </td>
    </tr>
  );
}

function SpeciesBreakdownRow({ week }: { week: SpeciesForecastWeek }) {
  return (
    <tr
      className={`bg-white dark:bg-[#1e2521] ${
        week.projectedQuantity === 0 ? 'bg-red-50 dark:bg-red-900/10' : ''
      }`}
    >
      <td className="px-4 py-2 text-stone-700 dark:text-stone-300">
        {format(parseISO(week.weekStarting), 'MMM d')}
      </td>
      <td className="px-4 py-2 text-right text-stone-600 dark:text-stone-400">
        {week.workshopsCount > 0 ? week.workshopsCount : '—'}
      </td>
      <td className="px-4 py-2 text-right text-stone-600 dark:text-stone-400">
        {week.consumptionThisWeek > 0 ? week.consumptionThisWeek.toLocaleString() : '—'}
      </td>
      <td className="px-4 py-2 text-right">
        <BalanceCell value={week.projectedQuantity} />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function BalanceCell({ value }: { value: number }) {
  if (value <= 0) {
    return <span className="font-medium text-red-600 dark:text-red-400">0</span>;
  }
  return <span className="text-stone-700 dark:text-stone-300">{value.toLocaleString()}</span>;
}

// ---------------------------------------------------------------------------
// Stockout status helpers
// ---------------------------------------------------------------------------

interface StockoutStatus {
  level: number; // 0 = ok, 1 = amber, 2 = red
  Icon: typeof CheckCircle;
  iconClass: string;
  textClass: string;
  borderClass: string;
  badgeClass: string;
}

function getStockoutStatus(date: string | null): StockoutStatus {
  if (date === null) {
    return {
      level: 0,
      Icon: CheckCircle,
      iconClass: 'text-emerald-500',
      textClass: 'text-emerald-600 dark:text-emerald-400',
      borderClass: 'border-stone-200 dark:border-stone-700',
      badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    };
  }

  const daysUntil = Math.ceil(
    (parseISO(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );

  if (daysUntil <= 14) {
    return {
      level: 2,
      Icon: XCircle,
      iconClass: 'text-red-500',
      textClass: 'text-red-600 dark:text-red-400',
      borderClass: 'border-red-200 dark:border-red-800',
      badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
  }

  return {
    level: 1,
    Icon: AlertTriangle,
    iconClass: 'text-amber-500',
    textClass: 'text-amber-600 dark:text-amber-400',
    borderClass: 'border-amber-200 dark:border-amber-700',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
}
